/**
 * WalletPassBadge — the "Add to Apple Wallet" control, as Apple's own artwork.
 *
 * WHY THIS IS A COMPONENT AND NOT A BUTTON WITH A WALLET ICON
 *
 * "Add to Apple Wallet" is not a label we may set on an ordinary button. It is
 * a badge Apple licenses, ships as artwork per locale, and publishes rules for:
 * the wordmark and logo may not be redrawn, recoloured, rotated, shadowed or
 * animated, and the badge needs clear space around it. A hand-drawn look-alike
 * on an outline button is the failure mode this component exists to prevent —
 * so the artwork is served byte-identical from the static tree, and nothing in
 * here touches its pixels.
 *
 * Lifted from the registration confirmation panel (`landing-builder-helpers`,
 * PR #842) so both surfaces render the SAME control rather than each growing
 * its own approximation. The comments below are that implementation's, kept
 * because every one of them records a real defect.
 *
 * MISSING ARTWORK FALLS BACK TO `fallback`, NEVER TO A BROKEN IMAGE OR A
 * HAND-DRAWN BADGE. Locales Apple has not shipped us yet fall to `en`; if that
 * is missing too, the surface gets its ordinary wallet control and picks up the
 * real badge the moment the file lands, with no code change.
 */
import { useState, type JSX, type ReactNode } from "react";
import { currentLocale } from "./i18n";

/** WHERE THE ARTWORK IS SERVED FROM.
 *
 *  Production: the app owns its origin, so the badge is at `/wallet-badge/…`.
 *  A path RELATIVE to the document would be wrong there — the designer runs at
 *  `/journeys/confirmation`, which would resolve to `/journeys/wallet-badge/…`.
 *  Root-absolute is the only correct production answer.
 *
 *  The design harness is the exception: it serves the whole project under
 *  `…/v1/design/projects/<id>/serve/`, and a root-absolute URL skips that prefix
 *  and 404s — which is precisely how this badge silently fell back to its plain
 *  control after the artwork had already been added. The prefix is read off the
 *  document URL AS LOADED (the navigation entry survives the SPA's own routing,
 *  which `document.baseURI` does not), so nothing has to be configured and
 *  production is untouched. */
const assetBase = (): string => {
  if (typeof window === "undefined") return "/";
  const override = (window as unknown as { __CD_ASSET_BASE__?: string }).__CD_ASSET_BASE__;
  if (typeof override === "string" && override) return override;
  const nav = performance.getEntriesByType("navigation")[0] as { name?: string } | undefined;
  const served = nav && nav.name ? /^(.*\/serve\/)/.exec(nav.name) : null;
  return served ? served[1] : `${window.location.origin}/`;
};
const badgeUrl = (name: string) => {
  const base = assetBase();
  const path = `wallet-badge/${name}.svg`;
  return base === "/" ? `/${path}` : new URL(path, base).toString();
};

/** Apple publishes no absolute minimum; 44px is our touch-target floor, and the
 *  width follows the artwork's own 3.163 ratio (139.2px). Clear space is the
 *  published 0.1×height, written against the same constant so the two move
 *  together if the height ever changes. */
const BADGE_H = 44;

export function WalletPassBadge(props: {
  /** The `.pkpass` download. Absent = the designer's inert preview: the badge is
   *  drawn (it is what the section can carry) but goes nowhere. */
  href?: string;
  /** Rendered instead when the artwork cannot be loaded. */
  fallback?: ReactNode;
  /** The line under the badge. Absent = no line. */
  note?: string;
}): JSX.Element | null {
  const [failed, setFailed] = useState(false);
  if (failed) return <>{props.fallback ?? null}</>;

  /* A FILE DOWNLOAD (application/vnd.apple.pkpass), not a navigation, hence
     `download`. The URL carries a management token in its query string, so it
     is treated as any credential-bearing URL: no analytics binding, no
     logging, referrer suppressed. */
  const badge = (
    <img
      src={badgeUrl(currentLocale())}
      onError={(e) => {
        /* Anything outside the 34 shipped locales falls back to en. Guarded so
           a missing en.svg cannot loop — and a missing en.svg is also how this
           component learns the artwork is not deployed at all, which is when
           `fallback` takes over. de-CH and qps-ploc are real files at those
           names (DE and US_UK artwork respectively — Apple ships no variant for
           either), so they resolve directly and no mapping table is needed. */
        const img = e.currentTarget;
        if (img.dataset.fallback) { setFailed(true); return; }
        img.dataset.fallback = "1";
        img.src = badgeUrl("en");
      }}
      alt=""
      height={BADGE_H}
      style={{ height: BADGE_H, width: "auto", maxWidth: "100%", display: "block" }}
    />
  );

  /* No filter, no transform, no hover recolour — a pressed or hover treatment
     belongs on the anchor, never on the artwork. An SVG optimiser on this path
     strips the badge's <style> block and it renders as a solid black slab:
     geometry intact, wordmark and logo invisible. That failure is invisible to
     reading the file and only shows up in a render. */
  const frame: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    padding: BADGE_H * 0.1, boxSizing: "content-box",
    maxWidth: "100%", textDecoration: "none",
  };

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
      {props.href ? (
        <a href={props.href} download rel="noreferrer" referrerPolicy="no-referrer"
          aria-label="Add to Apple Wallet" style={frame}>{badge}</a>
      ) : (
        <span role="img" aria-label="Add to Apple Wallet" style={frame}>{badge}</span>
      )}
      {props.note ? (
        <span style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{props.note}</span>
      ) : null}
    </span>
  );
}
